<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { VTextField } from '@/modules/shared/components'

const INDUSTRIES = [
  'AgTech',
  'AI / ML',
  'Biotech',
  'Climate',
  'Consumer',
  'Crypto / Web3',
  'Cybersecurity',
  'Deep Tech',
  'EdTech',
  'Enterprise',
  'Fintech',
  'Gaming',
  'Hardware',
  'Health Tech',
  'InsurTech',
  'Logistics',
  'Marketplace',
  'Media',
  'Real Estate',
  'SaaS',
  'Other...',
]

const PREDEFINED = INDUSTRIES.filter((i) => i !== 'Other...')

const props = defineProps<{
  modelValue: {
    industries_interested: string[]
  }
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: typeof props.modelValue): void
}>()

const interested = ref<string[]>([])
const otherText = ref('')

const isOtherSelected = computed(() => interested.value.includes('Other...'))

// Restore local UI state from the stored value — custom text lives in the array
// alongside predefined picks; we split it back into otherText + the "Other..." pill.
const initFromModel = (industries: string[]) => {
  const custom = industries.find((i) => !PREDEFINED.includes(i) && i !== 'Other...')
  if (custom) {
    otherText.value = custom
    const rest = industries.filter((i) => i !== custom)
    interested.value = rest.includes('Other...') ? [...rest] : [...rest, 'Other...']
  } else {
    otherText.value = ''
    interested.value = [...industries]
  }
}

initFromModel(props.modelValue.industries_interested)

watch(
  () => props.modelValue,
  (val) => {
    initFromModel(val.industries_interested)
  },
  { deep: true },
)

const emitUpdate = () => {
  const industries = interested.value.filter((i) => i !== 'Other...')
  if (isOtherSelected.value && otherText.value.trim()) {
    industries.push(otherText.value.trim())
  }
  emit('update:modelValue', {
    industries_interested: industries,
  })
}

const toggleInterested = (industry: string) => {
  const idx = interested.value.indexOf(industry)
  if (idx > -1) {
    interested.value.splice(idx, 1)
  } else {
    interested.value.push(industry)
  }
  if (industry === 'Other...' && idx > -1) {
    otherText.value = ''
  }
  emitUpdate()
}

const isValid = async () => true

defineExpose({ isValid })
</script>

<template>
  <div>
    <div class="flex flex-wrap gap-2">
      <button
        v-for="industry in INDUSTRIES"
        :key="'int-' + industry"
        type="button"
        class="cursor-pointer select-none whitespace-nowrap rounded-full border-none bg-white px-[18px] py-[9px] font-[inherit] text-[13px] font-medium shadow-sm ring-1 transition-shadow duration-150 ease-[cubic-bezier(0.22,1,0.36,1)] enabled:active:translate-y-px enabled:active:shadow-sm disabled:cursor-not-allowed disabled:opacity-35"
        :class="
          interested.includes(industry)
            ? 'bg-[#009192]/[0.08] text-[#009192] ring-[#009192] hover:shadow-md'
            : 'text-gray-700 ring-gray-700/20 hover:shadow-md hover:ring-gray-700/30'
        "
        @click="toggleInterested(industry)"
      >
        {{ industry }}
      </button>
    </div>

    <div v-if="isOtherSelected" class="mt-4">
      <VTextField v-model="otherText" placeholder="Tell us more..." @input="emitUpdate" />
    </div>
  </div>
</template>
